/// <reference path="../../typings/react-jss.d.ts" />
/// <reference path="../../typings/jss-preset-default.d.ts" />
import originalInjectSheet, {
  Classes,
  customInjectSheet,
  JssProvider as OriginalJssProvider,
  JssProviderProps
} from 'react-jss'
import {create} from 'jss'
import {compose, hoistStatics, mapProps, setDisplayName, wrapDisplayName} from 'recompose'
import defaultPreset from 'jss-preset-default'
import cn from 'classnames'
import {mergeClasses} from '../jss'
import {generatePropertyName} from '../react-util'
import defaultProps from 'recompose/defaultProps'

/**
 * Добавляет `classes` для внешних `Props`.
 *
 * @example
 *
 * type OuterProps = WithPartialClasses<typeof styles> & {
 *   // ...
 * }
 */
export type WithPartialClasses<T> = {
  classes?: Partial<Classes<T>>
  className?: string
}

const EXTERNAL_CLASSES = generatePropertyName()

const nameManager: {[k: string]: string | boolean} = {}

const mergeClassesWithClassName = (classes: any | undefined, className: string | undefined): any | undefined => {
  if (!classes) {
    if (!className) {
      return undefined
    }

    return {root: className}
  }
  if (className) {
    return {
      ...classes,
      root: cn(className, classes.root)
    }
  }

  return classes
}

const injectSheet: typeof customInjectSheet = (stylesOrSheet, componentName, options) => {
  // Check if this `componentName` is already used.
  // We have two strategies: with HMR and witout.
  if ((module as any).hot) {
    // If it's HMR one file can be initialized multiple times.
    // That's why we check the callee by the call stack.
    const errorStackParser = require('error-stack-parser') as typeof import ('error-stack-parser')
    const callStack = errorStackParser.parse(new Error())
    // We set callee as the prev callframe from this one.
    // It might be problem when `injectSheet` is used in other HOC.
    const callee = callStack[1]

    if (nameManager[componentName] === undefined) {
      nameManager[componentName] = callee && callee.fileName || true
    }

    const prevCalleeFilename = nameManager[componentName]

    // If other file already use this `componentName` we throw an error.
    if (!callee || callee.fileName !== prevCalleeFilename) {
      throw new Error(`${componentName} already exists in sheets - please, pick unique component name. It's already used in ${prevCalleeFilename}`)
    }
  } else {
    // If this `componentName` is already used by someone we throw an error.
    if (nameManager[componentName]) {
      throw new Error(`${componentName} already exists in sheets - please, pick unique component name.`)
    }

    nameManager[componentName] = true
  }

  return (OriginalComponent) => (
    hoistStatics(
      compose(
        setDisplayName(wrapDisplayName(OriginalComponent, 'injectSheet')),
        mapProps(({classes, className, ...props}: any) => ({
          ...props,
          [EXTERNAL_CLASSES]: mergeClassesWithClassName(classes, className)
        })),
        // TODO(SuperPaintman): that's strange, because bare `options` is ok here.
        originalInjectSheet(stylesOrSheet, {...options, componentName}),
        mapProps(({classes, [EXTERNAL_CLASSES]: externalClasses, ...props}: any) => ({
          ...props,
          classes: externalClasses === undefined
            ? classes
            : mergeClasses(classes, externalClasses)
        }))
      )
    )(OriginalComponent) as any
  )
}

export default injectSheet

export const jss = create(defaultPreset())

export const JssProvider = defaultProps<Partial<JssProviderProps<any>>>({
  jss
})(OriginalJssProvider)

export * from 'react-jss' // tslint:disable-line:import-blacklist
